import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { LoadingStatus } from '@/enums/LoadingStatus.enum';
import { CheckpointCheck } from '@/types/CheckpointCheck.type';
import { setCheckpointChecks, setEntityStatus } from '@/store/slices/masterDataSlice';
import { masterDataIdbService } from '@/store/idbStorage/masterDataIdbService';
import type { MasterDataEntityKey } from '@/store/idbStorage/masterDataIdbService';
import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { clivApiRequest } from '@/utils/apiClient';
import { systemConfig } from '@/config/systemConfig';
import { captureApiFailure } from '@/utils/sentryApiError.util';
import {
  MasterDataHookOptions,
  shouldUseMasterDataCache,
} from '@/hooks/masterData/masterDataHookOptions';

const ENTITY_KEY: MasterDataEntityKey = 'md_checkpointChecks';

export const useCheckpointChecks = (options?: MasterDataHookOptions) => {
  const refreshIfEmpty = options?.refreshIfEmpty ?? false;
  const dispatch = useDispatch();
  const checkpointChecks = useAppSelector((state: RootState) => state.masterData.checkpointChecks);
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus['checkpointChecks'] ?? LoadingStatus.IDLE
  );

  useEffect(() => {
    if (status !== LoadingStatus.IDLE) {
      return;
    }
    const load = async () => {
      dispatch(setEntityStatus({ entity: 'checkpointChecks', status: LoadingStatus.LOADING }));
      try {
        const cached = await masterDataIdbService.getEntity<CheckpointCheck[]>(ENTITY_KEY);
        if (shouldUseMasterDataCache(cached, refreshIfEmpty)) {
          dispatch(setCheckpointChecks(cached));
          dispatch(
            setEntityStatus({ entity: 'checkpointChecks', status: LoadingStatus.SUCCEEDED })
          );
          return;
        }
        const response = await clivApiRequest<CheckpointCheck[]>({
          url: `${systemConfig.apiUrl}/${API_ENDPOINTS.META.CHECKPOINT_CHECKS}`,
        });
        if (response.ok) {
          const data = response.data || [];
          dispatch(setCheckpointChecks(data));
          await masterDataIdbService.setEntity(ENTITY_KEY, data);
          dispatch(
            setEntityStatus({ entity: 'checkpointChecks', status: LoadingStatus.SUCCEEDED })
          );
        } else {
          captureApiFailure('Error fetching checkpoint checks', { status: response.status });
          dispatch(setEntityStatus({ entity: 'checkpointChecks', status: LoadingStatus.FAILED }));
        }
      } catch (error) {
        captureApiFailure('Error fetching checkpoint checks', { error });
        dispatch(setEntityStatus({ entity: 'checkpointChecks', status: LoadingStatus.FAILED }));
      }
    };
    void load();
  }, [status, dispatch, refreshIfEmpty]);

  return { checkpointChecks, status };
};
